/**
 * File upload / image upload
 */
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import classNames from 'classnames';
import React, { ReactElement, useEffect, useState } from 'react';
import IosClose from 'react-ionicons/lib/IosClose';
import { blobToUrl } from 'utils/image-file';
import ImageUrlResizer from 'utils/image-url-resizer';
import { useIsScreenSizeMax } from 'utils/screen-size';

import ImageCrop from '../ImageCrop/ImageCrop';
import Loader from '../Loader/Loader';
import Modal from '../Modal/Modal';

type Props = {
  accept?: string;
  preview?: boolean;
  onChange: (files: FileList | File[]) => void;
  onError?: () => void;
  onRemoveImage?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onSetImagePreviewSrc?: (imagePreviewSrc: File | Blob | string) => void;
  wrapperClassName?: string;
  inputClassName?: string;
  imgClassName?: string;
  multiple?: boolean;
  size?: number;
  imageFilled?: boolean;
  icon?: ReactElement;
  iconSize?: number;
  shape?: 'round' | 'rect';
  initialImage?: File | Blob | string;
  croppingEnabled?: boolean;
  disabled?: boolean;
  removeButton?: boolean;
};

const FileUpload = (props: Props) => {
  const {
    accept,
    preview,
    onChange,
    onError,
    onRemoveImage,
    wrapperClassName,
    inputClassName,
    imgClassName,
    multiple,
    size,
    imageFilled,
    icon,
    iconSize,
    shape,
    initialImage,
    croppingEnabled,
    disabled,
    removeButton,
    onSetImagePreviewSrc,
  } = props;

  const isXSmall = useIsScreenSizeMax('sm');

  const [dropped, setDropped] = useState(false);

  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const [addedImage, setAddedImage] = useState<File>();

  const [isLoading, setLoading] = useState(false);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [, setCroppedImage] = useState<Blob>();

  const acceptOption = accept || 'image/png, image/jpeg, image/jpg, image/gif, image/bmp, image/webp, image/heic, image/heif';
  const previewOption = preview === undefined ? true : preview;
  const shapeOption = shape || 'round';
  const croppingEnabledOption = croppingEnabled === undefined ? false : croppingEnabled;
  const iconOption = React.cloneElement(icon || <AddIcon />, {
    className: 'add-icon',
    style: {
      fontSize: `${Math.max(iconSize || size - 20, 0)}px`,
    },
  });

  // Preview image before upload
  const previewImage = (imageFile: File | Blob | string) => {
    if (!previewOption) {
      return;
    }

    // Set image src
    if (typeof imageFile === 'string') {
      setPreviewImageSrc(imageFile);
    } else if (imageFile && isImage(imageFile)) {
      // Read image from file
      const fileReader: FileReader = new FileReader();

      fileReader.onload = (event: Event) => {
        const target: EventTarget & { result?: string } = event.target;

        setPreviewImageSrc(target.result);
      };

      // convert to base64 string
      fileReader.readAsDataURL(imageFile);
    } else {
      // Remove if nothing passed
      setPreviewImageSrc(null);
    }
  };

  // On add image
  const onAddImage = (event) => {
    const files = event.target.files || event.srcElement.files;
    const file = files[0];

    if (file) {
      setAddedImage(file);

      // Preview image
      if (!croppingEnabled && !multiple) {
        previewImage(file);
      }

      if (croppingEnabledOption && isImage(file)) {
        setCropModalOpen(true);
      } else {
        // Callback immediately if no crop
        onChange(files);
      }
    } else if (typeof onError === 'function') {
      // Trigger upload error callback
      onError();
    }
  };

  const onImageCrop = (newCroppedImage) => {
    // Set cropped image
    setCroppedImage(newCroppedImage);

    // Preview
    previewImage(blobToUrl(newCroppedImage));

    // Close modal
    setCropModalOpen(false);

    // Callback when crop done
    onChange([newCroppedImage]);
  };

  // Load initial image
  useEffect(() => {
    previewImage(initialImage);
  }, [initialImage]);

  useEffect(() => {
    onSetImagePreviewSrc?.(previewImageSrc);
  }, [previewImageSrc]);

  return (
    <>
      <label
        className={classNames('file-upload', wrapperClassName, {
          dropped,
          previewed: previewImageSrc,
          disabled,
        })}
        htmlFor="file-input"
        style={{
          width: size && `${size}px`,
          minWidth: size && `${size}px`,
          height: size && `${size}px`,
          minHeight: size && `${size}px`,
          borderRadius: shapeOption === 'round' ? 999 : 4,
        }}
        onDragEnter={() => setDropped(true)}
        onDragLeave={() => setDropped(false)}
      >
        {/* image preview */}
        <div
          className={classNames('uploaded-image', imgClassName, {
            previewed: previewImageSrc,
          })}
          style={{
            backgroundImage: previewImageSrc && `url(${ImageUrlResizer.resize(previewImageSrc, 300)})`,
            borderRadius: shapeOption === 'round' ? 999 : 4,
          }}
        />

        {/* add icon */}
        {!previewImageSrc && iconOption}

        {/* upload button */}
        <input
          className={classNames('file-input', inputClassName)}
          type="file"
          value=""
          title=""
          multiple={multiple}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
            }
          }}
          onChange={onAddImage}
          accept={acceptOption}
        />
      </label>

      {/* CROP MODAL */}
      <Modal
        open={cropModalOpen}
        closeIconVisible={true}
        onClose={(_event, reason) => {
          if (reason !== 'backdropClick') {
            setAddedImage(null);
            setPreviewImageSrc(null);
            setCropModalOpen(false);
          }
        }}
        className="image-crop-modal"
        contentClassName="image-crop-modal-content"
        disableEscapeKeyDown={true}
        fullWidth={true}
        maxWidth="sm"
      >
        <ImageCrop file={addedImage} onSuccess={onImageCrop} onError={() => setCropModalOpen(false)} isLoading={setLoading} />

        {isLoading && <Loader className="mt-0-i" />}
      </Modal>

      {imageFilled && removeButton && onRemoveImage ? (
        <Button
          variant="outlined"
          className="remove-image-button"
          onClick={(e) => {
            e.stopPropagation();
            onRemoveImage(e);
          }}
        >
          <IosClose fontSize={isXSmall ? '25px' : '35px'} />
        </Button>
      ) : null}
    </>
  );
};

export function isImage(file: File | Blob) {
  return file instanceof Blob || (file as Blob)?.type?.startsWith('image');
}

export default FileUpload;
